import { useSession } from "@/admin/lib/auth-client";
import { useState, useEffect } from "react";
import { getClientEnv, isDevAuthBypass } from "@nosible/shared";
import { AccessDenied } from "./AccessDenied";
import { trpc } from "@/admin/lib/trpc";

export function ProtectedRoute({
  children,
  requireAdmin = false,
}: {
  children: React.ReactNode;
  requireAdmin?: boolean;
}) {
  // DEV-ONLY: when PUBLIC_DEV_AUTH_BYPASS=1, skip both the no-session redirect
  // and the tRPC isAdmin check (the API backend isn't running locally during
  // UI-only review). Build-time flag — stripped from production builds.
  const devBypass = isDevAuthBypass();
  const { data: session, isPending } = useSession();
  const [hasAdminPermission, setHasAdminPermission] = useState<boolean | null>(
    devBypass ? true : null,
  );
  const { data: adminCheck, isLoading: isAdminCheckLoading } =
    trpc.admin.isAdmin.useQuery(undefined, {
      enabled: !devBypass && requireAdmin && !!session,
      retry: false,
    });

  useEffect(() => {
    if (devBypass) return;
    if (!isPending && !session) {
      const currentUrl = window.location.href;
      const loginUrl = getClientEnv()?.loginUrl;
      window.location.href = `${loginUrl}?redirect=${encodeURIComponent(currentUrl)}`;
    }
  }, [session, isPending, devBypass]);

  useEffect(() => {
    if (devBypass) return;
    if (!requireAdmin) {
      setHasAdminPermission(true);
      return;
    }

    if (session) {
      if (adminCheck) {
        setHasAdminPermission(adminCheck.isAdmin);
      } else if (!isAdminCheckLoading) {
        setHasAdminPermission(false);
      }
    }
  }, [session, requireAdmin, adminCheck, isAdminCheckLoading, devBypass]);

  if (isPending || (requireAdmin && hasAdminPermission === null)) {
    return (
      <div className="flex h-screen items-center justify-center bg-obsidian">
        <div className="text-silica">Loading...</div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex h-screen items-center justify-center bg-obsidian">
        <div className="text-silica">Redirecting to login...</div>
      </div>
    );
  }

  if (requireAdmin && !hasAdminPermission) {
    return <AccessDenied />;
  }

  return <>{children}</>;
}
